import { NgFor } from '@angular/common';
import { Component, ElementRef, HostListener, OnDestroy, OnInit, ViewChild } from '@angular/core';
import { MatDialog } from '@angular/material/dialog';
import { MatIconModule } from '@angular/material/icon';
import { MatMenuModule } from '@angular/material/menu';
import { ActivatedRoute } from '@angular/router';
import { GetMember, GetMemberResponse } from '@edtech-ui/core/models/members/teacher';
import { ClassroomManagementService } from '@edtech-ui/core/services/classroom-management-service/classroom-management.service';
import { MemberManagementService } from '@edtech-ui/core/services/member-management-services/member-management.service';
import { isValidEmail } from '@edtech-ui/core/utils/validators';
import { SharedModule } from '@edtech-ui/shared/shared.module';
import { ToastrService } from 'ngx-toastr';
import { catchError, finalize, of, Subscription, tap } from 'rxjs';
import { DeleteMemberDialogBoxComponent } from '../delete-member-dialog-box/delete-member-dialog-box.component';
import { usersRolesTeacher } from '@edtech-ui/constants/users-roles-teachers.enum';
import { RemoveMemberPayload } from '@edtech-ui/core/models/members/delete-member';

@Component({
	selector: 'app-classroom-members',
	standalone: true,
	imports: [MatIconModule, MatMenuModule, NgFor, SharedModule],
	templateUrl: './classroom-members.component.html',
	styleUrl: './classroom-members.component.scss'
})
export class ClassMembersComponent implements OnInit ,OnDestroy {
	showInvite = false;
	inviteEmail = '';
	emailError = false;
	showStudentInvite = false;
	studentInviteEmail = '';
	studentEmailError = false;
	memberId: number = 0;
	_teachers: GetMember[] = [];
	_students: GetMember[] = [];
	isLoading: boolean = false;
	userId: number = 0;
	usersRolesTeacher = usersRolesTeacher;
	subscription = new Subscription();
	isSending:boolean = false;

	@ViewChild('teacherPopup') teacherPopupRef!: ElementRef;
	@ViewChild('teacherContainer') teacherContainerRef!: ElementRef;
	@ViewChild('studentPopup') studentPopupRef!: ElementRef;
	@ViewChild('studentContainer') studentContainerRef!: ElementRef;

	constructor(
		private _memberService: MemberManagementService,
		private _classroomService: ClassroomManagementService,
		private _toastr: ToastrService,
		private route: ActivatedRoute,
		private _dialog: MatDialog
	) { }


	ngOnInit(): void {
		const id = this.route.parent?.snapshot.paramMap.get('id');
		if (id) {
			this.memberId = Number(id);
			this.getMembers(this.memberId)
		}
	}

	getMembers(memberId: number) {
		this.isLoading = true;
		this.subscription.add(
		this._memberService.getMembers(memberId).pipe(
			tap((response: GetMemberResponse) => {
				if (response.Success) {
					this.isLoading = false;
					this._teachers = response.Data.teachers;
					this._students = response.Data.students;
				}else{
					this._toastr.error(response.Message);
				}
			}),
			catchError (() =>{
				this.isLoading=false;
				this._toastr.error('Something went wrong while fetching the Members');
				return of(null);
			})
		).subscribe());
	}

	sendInvite() {
		if (!isValidEmail(this.inviteEmail)) {
			this.emailError = true;
			return;
		}
		this.isSending = true;
		const payload = {
			email: this.inviteEmail,
			classroom_code: this._classroomService.class_code.value,
			class_id: this.memberId
		};
		this.subscription.add(
		this._memberService.postInviteStudent(payload).pipe(
			tap(response => {
				if (response.Success) {
					this.isSending = false;
					this._toastr.success(response.Message);
					this.inviteEmail = '';
					this.showInvite = false;
					this.emailError = false;
					this.getMembers(this.memberId);
				} else {
					this._toastr.warning(response.Message);
				}
			}),
			catchError (() =>{
				this.isSending = false;
				this._toastr.error('Something went wrong');
				return of(null);
			})
		).subscribe());
	}


	cancelInvite() {
		this.showInvite = false;
		this.inviteEmail = '';
		this.emailError = false;
	}

	sendStudentInvite() {
		if (!isValidEmail(this.studentInviteEmail)) {
			this.studentEmailError = true;
			return;
		}
		this.studentEmailError = false;
		this.isSending = true;

		const payload = {
			classroom_code: this._classroomService.class_code.value,
			email: this.studentInviteEmail,
			class_id: this.memberId
		};
		this.subscription.add(
		this._memberService.postInviteStudent(payload).pipe(
			tap(response => {
				if (response.Success) {
					this.isSending = false;
					this._toastr.success(response.Message);
					this.studentInviteEmail = '';
					this.showStudentInvite = false;
					this.getMembers(this.memberId);
				} else {
					this._toastr.warning(response.Message);
				}
			}),
			catchError (() =>{
				this.isSending = false;
				this._toastr.error('Something went wrong');
				return of(null);
			}),
			finalize(()=>{
				this.isSending = false;
			})
		).subscribe());
	}

	cancelStudentInvite() {
		this.studentInviteEmail = '';
		this.studentEmailError = false;
		this.showStudentInvite = false;
	}

	// toggleTeacherInvite() {
	// 	this.showInvite = !this.showInvite;
	// 	if (this.showInvite) this.showStudentInvite = false;
	// }

	toggleStudentInvite() {
		this.showStudentInvite = !this.showStudentInvite;
		if (this.showStudentInvite) this.showInvite = false;
	}

	@HostListener('document:click', ['$event'])
	onDocumentClick(event: MouseEvent) {
		const target = event.target as HTMLElement;

		if (
			this.showInvite &&
			this.teacherPopupRef &&
			this.teacherContainerRef &&
			!this.teacherPopupRef.nativeElement.contains(target) &&
			!this.teacherContainerRef.nativeElement.contains(target)
		) {
			this.cancelInvite();
		}

		if (
			this.showStudentInvite &&
			this.studentPopupRef &&
			this.studentContainerRef &&
			!this.studentPopupRef.nativeElement.contains(target) &&
			!this.studentContainerRef.nativeElement.contains(target)
		) {
			this.cancelStudentInvite();
		}
	}

	openDeleteMemberDialogBox(userType: usersRolesTeacher, userId: string) {
		const dialogRef = this._dialog.open(DeleteMemberDialogBoxComponent, {
			width: '400px',
			data: { type:userType }
		});

		dialogRef.afterClosed().subscribe(result => {
			if (result) {
				const payload: RemoveMemberPayload = {
					classroom_id: this.memberId,
					user_id: userId
				};
				this.deleteMembersApi(payload)
			}
		});
	}

	deleteMembersApi(payload: RemoveMemberPayload) {
		this.subscription.add(
		this._memberService.deleteMembers(payload).pipe(
			tap(response => {
				if (response.Success) {
					this._toastr.success(response.Message);
					this.getMembers(this.memberId);
				} else {
					this._toastr.error(response.Message);
				}
			}),
			catchError(() => {
				this._toastr.error('Something went wrong while fetching the members');
				return of(null);
			})
		).subscribe());
	}

	ngOnDestroy(): void {
		this.subscription.unsubscribe();
	}

}